import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'clsx';
import CheckCircle from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';
import { Radio } from '@material-ui/core';
import { Collapse } from '@material-ui/core';
import { InformationOutline } from 'mdi-material-ui';
import RadioboxMarked from 'mdi-material-ui/RadioboxMarked';

import { InsurersEnum, LabelEnum, InstallmentsEnum } from 'app/shared/enums';
import { getOffer } from 'app/store/data/quotationResult';
import {
  selectSelectedOfferId,
  selectSelectedOfferInsurerId,
} from 'app/store/data/selectedOffer';
import { MubiInstallmentsModal } from 'app/components/MubiInstallmentsModal';
import { NewButton } from 'app/components/NewButton';
import { ErrorModal } from 'app/components/ErrorModal';
import { selectIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';
import { selectIsPaymentMethodOnInstallmentsActive } from 'app/store/data/quotationResult/selectors';

import { MubiInstallmentsRadioOptionProps } from './MubiInstallmentsRadioOption.interfaces';
import { useStyles } from './MubiInstallmentsRadioOption.styles';

import { MubiInstallmentsContext } from './useMubiInstallments.context';
import { useMubiInstallments } from './useMubiInstallments';

export default function MubiInstallmentsRadioOption(
  props: MubiInstallmentsRadioOptionProps
) {
  const { checked, mubiInstallments, onClick, hasError } = props;

  const classes = useStyles();
  const insurerId = useSelector(selectSelectedOfferInsurerId);
  const offerId = useSelector(selectSelectedOfferId);
  const isFormMicroCenterActive = useSelector(selectIsFormMicroCenterActive);
  const offer = useSelector(getOffer(offerId));
  const isCheckoutPaymentFormFlow = useSelector(
    selectIsPaymentMethodOnInstallmentsActive
  );

  const {
    closeModal,
    openModal,
    goToSelectedStep: setActiveStep,
    mubiInstallmentsRrsoExpanded,
    toggle,
    activeStep,
    isModalOpen,
    showAllerMubiInstallmentsApplicationNotFilled,
    errorOnModal,
    setErrorOnModal,
    isRejected,
    hiProConsents,
    setHiProConsents,
    installmentOffer,
    setInstallmentOffer,
    hiProToken,
    setHiProToken,
    setHiProInstalmentContractConfirmed,
    setIsRejected,
    setShowAlertMubiInstallmentsApplicationNotFilled,
    hiProInstalmentContractConfirmed,
    mubiInstallmentsDataLayerEvent,
  } = useMubiInstallments(offer);

  const rrso = installmentOffer
    ? installmentOffer.rrso
    : mubiInstallments.installmentsInfo?.internalInstallmentsRrso;
  const internalInstallmentsCount = installmentOffer
    ? installmentOffer.installmentCount
    : mubiInstallments.installmentsInfo?.internalInstallmentsCount;

  const internalInstallmentPrice = installmentOffer
    ? installmentOffer.installmentAmount
    : mubiInstallments.installmentsInfo?.internalInstallmentPrice;

  const isMubiInstalmentRejected =
    isRejected || offer?.internalInstallmentsEstimation?.rejected;

  const expandingPointMark = '^^';

  const rrsoDesc =
    insurerId === InsurersEnum.Trasti
      ? isCheckoutPaymentFormFlow
        ? InstallmentsEnum.RrsoDescriptionTrastiCheckoutPaymentFormFlow
        : InstallmentsEnum.RrsoDescriptionTrasti
      : InstallmentsEnum.RrsoDescription;

  const rrsoExpandable: boolean = rrsoDesc.includes(expandingPointMark);

  const rrsoDescFormatted =
    rrsoExpandable && !mubiInstallmentsRrsoExpanded
      ? `${rrsoDesc.split(expandingPointMark)[0]}...`
      : rrsoDesc.replace(expandingPointMark, '');

  const handleRrsoExpandableClick = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    e.stopPropagation();
    return toggle();
  };

  const getInstallmentsUnavailableInfo = () => {
    const { Trasti, Axa, Wiener } = InsurersEnum;

    const NumberOfInstallments: Record<string, string> = {
      [Axa]: '10',
      [Trasti]: '6',
      [Wiener]: '10',
    };

    return (
      LabelEnum.InstallmentsUnavailableInfo1 +
      (NumberOfInstallments[insurerId] || '12') +
      LabelEnum.InstallmentsUnavailableInfo2
    );
  };

  const renderDetails = () => {
    if (isCheckoutPaymentFormFlow) {
      return (
        <div
          className={classNames(
            classes.installMentsDetailsDesc,
            classes.noMargin
          )}
        >
          <div className={classes.installmentsDetailsInline}>
            <span className={classes.installmentsDetails1}>
              {LabelEnum.OneInstallment}: {internalInstallmentPrice}
            </span>{' '}
            <span className={classes.installmentsDetails2}>
              {LabelEnum.NumberOfInstallments}: {internalInstallmentsCount},{' '}
              {LabelEnum.RRSO}: {rrso}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className={classes.installMentsDetailsDesc}>
        <div className={classes.column}>
          <div className={classes.greyBox}>
            {LabelEnum.OneInstallment}: {internalInstallmentPrice}
          </div>
          <div className={classes.greyBox}>
            {LabelEnum.NumberOfInstallments}: {internalInstallmentsCount}
          </div>
          <div className={classes.greyBox}>
            {LabelEnum.RRSO}: {rrso}
          </div>
        </div>
      </div>
    );
  };

  return (
    <MubiInstallmentsContext.Provider
      value={{
        hiProToken,
        setHiProToken,
        closeModal,
        setHiProConsents,
        hiProConsents,
        setInstallmentOffer,
        installmentOffer,
        setErrorOnModal,
        setHiProInstalmentContractConfirmed,
        setIsRejected,
        mubiInstallmentsDataLayerEvent,
        activeStep,
        setActiveStep,
      }}
    >
      <div
        tabIndex={isMubiInstalmentRejected ? -1 : 0}
        className={classNames(classes.controlContainer, 'focusVisibleOutline', {
          [classes.active]:
            checked && !showAllerMubiInstallmentsApplicationNotFilled,
          [classes.activeNew]:
            checked &&
            !showAllerMubiInstallmentsApplicationNotFilled &&
            isCheckoutPaymentFormFlow,
          [classes.error]:
            hasError || showAllerMubiInstallmentsApplicationNotFilled,
          [classes.disabled]: isMubiInstalmentRejected,
          [classes.rejectedNew]:
            isMubiInstalmentRejected && isCheckoutPaymentFormFlow,
        })}
        data-cy="mubi-installments-payment"
        onClick={isMubiInstalmentRejected ? undefined : onClick}
        onKeyDown={(e) => {
          if (
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            e.preventDefault();
            onClick();
          }
        }}
        {...(hasError || showAllerMubiInstallmentsApplicationNotFilled
          ? { 'data-field-error': 'true' }
          : {})}
      >
        {!isCheckoutPaymentFormFlow && (
          <div className={classes.mubiInstallmentsNewWrapper}>
            <div className={classes.mubiInstallmentsNewLabel}>
              {LabelEnum.New}
            </div>
          </div>
        )}
        {isMubiInstalmentRejected ? (
          isCheckoutPaymentFormFlow ? (
            <>
              <div
                className={classNames(classes.wrapper, classes.wrapperNew)}
                data-cy="mubi-installments-not-allowed"
              >
                <InformationOutline className={classes.icon} />
                {InstallmentsEnum.MubiInstallmentsRejected}
              </div>
              <div
                className={classNames(
                  classes.installmentsDetails,
                  classes.noPadding
                )}
              >
                <div className={classes.installMentsRrsoInfo}>
                  {renderDetails()}
                  <div className={classes.column}>
                    <div className={classes.installMentsRrsoInfo}>
                      {getInstallmentsUnavailableInfo()}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                className={classNames(classes.wrapper, {
                  [classes.wrapperNew]: isCheckoutPaymentFormFlow,
                })}
                data-cy="mubi-installments-not-allowed"
              >
                <InformationOutline className={classes.icon} />
                {InstallmentsEnum.MubiInstallmentsRejected}
              </div>
              <div
                className={classNames(classes.installmentsDetails, {
                  [classes.noPadding]: isCheckoutPaymentFormFlow,
                })}
              >
                <div className={classes.installMentsRrsoInfo}>
                  {renderDetails()}
                  <div className={classes.column}>
                    <div className={classes.installMentsRrsoInfo}>
                      {getInstallmentsUnavailableInfo()}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        ) : (
          <>
            <div
              className={classNames(classes.wrapper, {
                [classes.wrapperNew]: isCheckoutPaymentFormFlow,
              })}
            >
              <Radio
                tabIndex={-1}
                color={'primary'}
                checked={checked}
                checkedIcon={<RadioboxMarked />}
              />
              {InstallmentsEnum.MubiInstallments}
            </div>
            <div
              className={classNames(classes.installmentsDetails, {
                [classes.noPadding]: isCheckoutPaymentFormFlow,
              })}
            >
              <div className={classes.installMentsRrsoInfo}>
                {renderDetails()}
              </div>
              <div className={classes.column}>
                <div
                  className={classNames({
                    [classes.installMentsDetailsDesc]: checked,
                    [classes.noMargin]: checked && isCheckoutPaymentFormFlow,
                  })}
                >
                  {!isCheckoutPaymentFormFlow && (
                    <>
                      {InstallmentsEnum.Description1}
                      {internalInstallmentsCount}
                      {InstallmentsEnum.Description2}
                    </>
                  )}
                </div>
              </div>
              <Collapse in={checked}>
                {!isCheckoutPaymentFormFlow && (
                  <div className={classes.column}>
                    <div className={classes.installMentsRrsoInfo}>
                      {rrsoDescFormatted}
                      {rrsoExpandable && (
                        <>
                          &nbsp;
                          <span
                            className={classes.installMentsRrsoExpand}
                            onClick={handleRrsoExpandableClick}
                          >
                            {mubiInstallmentsRrsoExpanded
                              ? LabelEnum.Collapse
                              : LabelEnum.Expand}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {!isCheckoutPaymentFormFlow &&
                  !hiProInstalmentContractConfirmed && (
                    <div className={classes.buttonWrapper}>
                      <NewButton
                        variant={'contained'}
                        color={'primary'}
                        title={
                          activeStep < 2
                            ? InstallmentsEnum.OpenApplicationOnMubiInstallments
                            : InstallmentsEnum.FinishCompletingApplication
                        }
                        data-cy="apply-for-mubi-installments"
                        onClick={() => {
                          setShowAlertMubiInstallmentsApplicationNotFilled(
                            false
                          );
                          openModal();
                        }}
                        rounded={isFormMicroCenterActive}
                      />
                    </div>
                  )}
              </Collapse>
            </div>
          </>
        )}
        {isModalOpen && <MubiInstallmentsModal />}
        {errorOnModal && (
          <ErrorModal
            error={errorOnModal}
            onClose={() => setErrorOnModal(undefined)}
            buttonLabel={errorOnModal.btnLabel}
            action={() => setErrorOnModal(undefined)}
          />
        )}
      </div>

      {isCheckoutPaymentFormFlow && checked && !isMubiInstalmentRejected && (
        <span className={classes.checkoutPaymentFormFlowFooter}>
          {hiProInstalmentContractConfirmed && (
            <div
              className={classNames(
                classes.installmentsApproved,
                classes.checkoutPaymentFormFlowInstallmentsApproved
              )}
              data-cy="mubi-installment-confirmed"
            >
              <CheckCircle />
              <div className={classes.textWrapperCheckoutPaymentFlow}>
                <div className={classes.title}>
                  {InstallmentsEnum.MubiInstallmentsGranted}
                  <span>{InstallmentsEnum.ContinuePurchaseBelow}</span>
                </div>
              </div>
            </div>
          )}
          {!hiProInstalmentContractConfirmed && (
            <NewButton
              variant={'contained'}
              color={'primary'}
              title={
                activeStep < 2
                  ? InstallmentsEnum.OpenApplicationOnMubiInstallments
                  : InstallmentsEnum.FinishCompletingApplication
              }
              data-cy="apply-for-mubi-installments"
              onClick={() => {
                setShowAlertMubiInstallmentsApplicationNotFilled(false);
                openModal();
              }}
            />
          )}
          <div className={classes.column}>
            <div className={classes.installMentsRrsoInfoNew}>
              {rrsoDescFormatted}
              {rrsoExpandable && (
                <>
                  &nbsp;
                  <span
                    className={classes.installMentsRrsoExpand}
                    onClick={handleRrsoExpandableClick}
                  >
                    {mubiInstallmentsRrsoExpanded
                      ? LabelEnum.Collapse
                      : LabelEnum.Expand}
                  </span>
                </>
              )}
            </div>
          </div>
        </span>
      )}

      {hiProInstalmentContractConfirmed &&
        checked &&
        !isCheckoutPaymentFormFlow && (
          <div
            className={classes.installmentsApproved}
            data-cy="mubi-installment-confirmed"
          >
            <CheckCircle />
            <div className={classes.textWrapper}>
              <div className={classes.title}>
                {InstallmentsEnum.CongratulationsMubiInstalment}
              </div>
              <div>{InstallmentsEnum.ContinueCheckout}</div>
            </div>
          </div>
        )}
      {showAllerMubiInstallmentsApplicationNotFilled && (
        <div className={classes.installmentsApplicationNotFilled}>
          <ErrorOutlineRoundedIcon />
          <div className={classes.textWrapper}>
            <div>
              {activeStep < 2
                ? InstallmentsEnum.HiProApplicationNotFilled
                : InstallmentsEnum.HiProApplicationNotFilledontinue}
            </div>
          </div>
        </div>
      )}
    </MubiInstallmentsContext.Provider>
  );
}
